import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/lib/content";
import { CalendarDays, Clock, CheckCircle2, Loader2 } from "lucide-react";

const TIME_SLOTS = [
    "09:30 AM",
    "10:30 AM",
    "11:30 AM",
    "12:30 PM",
    "02:30 PM",
    "03:30 PM",
    "04:30 PM",
    "05:30 PM",
    "06:30 PM",
    "07:30 PM",
];

const todayIso = () => {
    const d = new Date();
    d.setDate(d.getDate());
    return d.toISOString().slice(0, 10);
};

export default function BookingDialog({ open, onOpenChange, initialService }) {
    const [submitting, setSubmitting] = React.useState(false);
    const [done, setDone] = React.useState(false);
    const [form, setForm] = React.useState({
        name: "",
        phone: "",
        email: "",
        service: initialService || SERVICES[0].title,
        preferred_date: todayIso(),
        preferred_time: TIME_SLOTS[1],
        notes: "",
    });

    React.useEffect(() => {
        if (initialService) {
            setForm((f) => ({ ...f, service: initialService }));
        }
    }, [initialService]);

    React.useEffect(() => {
        if (!open) {
            setDone(false);
        }
    }, [open]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please enter your name and phone number.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!payload.email) delete payload.email;
            if (!payload.notes) delete payload.notes;
            await api.post("/appointments", payload);
            setDone(true);
            toast.success("Appointment request received", {
                description:
                    "Our concierge will WhatsApp you a confirmation shortly.",
            });
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Could not submit — please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="booking-dialog"
                className="max-w-xl bg-[#faf9f6] border-[#e8e4db] rounded-3xl p-0 overflow-hidden"
            >
                {done ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-16 w-16 rounded-full bg-[#8fa998]/15 flex items-center justify-center mb-5">
                            <CheckCircle2
                                className="text-[#2d6a4f]"
                                size={32}
                                strokeWidth={1.5}
                            />
                        </div>
                        <h3 className="font-serif-display text-3xl text-[#114b5f]">
                            Appointment requested
                        </h3>
                        <p className="mt-3 text-[#4a5250]">
                            Thank you, {form.name.split(" ")[0]}. Our
                            concierge desk will confirm your slot on
                            WhatsApp within 30 minutes during clinic hours.
                        </p>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            data-testid="booking-close-success"
                            className="mt-8 rounded-full bg-[#114b5f] text-[#faf9f6] px-8 py-3 text-sm font-medium hover:bg-[#0d3a4a]"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="bg-[#114b5f] text-[#faf9f6] p-8">
                            <DialogHeader className="space-y-2 text-left">
                                <DialogTitle className="font-serif-display text-3xl">
                                    Reserve your chair
                                </DialogTitle>
                                <DialogDescription className="text-[#faf9f6]/75">
                                    Share a few details and we&apos;ll
                                    confirm your visit on WhatsApp.
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                        <form
                            onSubmit={submit}
                            data-testid="booking-form"
                            className="p-8 space-y-5"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label
                                        htmlFor="bk-name"
                                        className="text-xs uppercase tracking-wider text-[#4a5250]"
                                    >
                                        Full name
                                    </Label>
                                    <Input
                                        id="bk-name"
                                        data-testid="booking-form-name-input"
                                        value={form.name}
                                        onChange={(e) =>
                                            update("name", e.target.value)
                                        }
                                        placeholder="Your name"
                                        className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="bk-phone"
                                        className="text-xs uppercase tracking-wider text-[#4a5250]"
                                    >
                                        Phone
                                    </Label>
                                    <Input
                                        id="bk-phone"
                                        data-testid="booking-form-phone-input"
                                        value={form.phone}
                                        onChange={(e) =>
                                            update("phone", e.target.value)
                                        }
                                        placeholder="+91 98290 12345"
                                        className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <Label
                                    htmlFor="bk-email"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Email (optional)
                                </Label>
                                <Input
                                    id="bk-email"
                                    data-testid="booking-form-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        update("email", e.target.value)
                                    }
                                    placeholder="you@example.com"
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider text-[#4a5250]">
                                    Service
                                </Label>
                                <Select
                                    value={form.service}
                                    onValueChange={(v) =>
                                        update("service", v)
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="booking-form-service-trigger"
                                        className="mt-1 bg-white rounded-xl border-[#e8e4db] h-11"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {SERVICES.map((s) => (
                                            <SelectItem
                                                key={s.id}
                                                value={s.title}
                                            >
                                                {s.title}
                                            </SelectItem>
                                        ))}
                                        <SelectItem value="General consultation">
                                            General consultation
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label
                                        htmlFor="bk-date"
                                        className="text-xs uppercase tracking-wider text-[#4a5250] flex items-center gap-1"
                                    >
                                        <CalendarDays size={12} /> Preferred
                                        date
                                    </Label>
                                    <Input
                                        id="bk-date"
                                        data-testid="booking-form-date-input"
                                        type="date"
                                        min={todayIso()}
                                        value={form.preferred_date}
                                        onChange={(e) =>
                                            update(
                                                "preferred_date",
                                                e.target.value,
                                            )
                                        }
                                        className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-[#4a5250] flex items-center gap-1">
                                        <Clock size={12} /> Preferred time
                                    </Label>
                                    <Select
                                        value={form.preferred_time}
                                        onValueChange={(v) =>
                                            update("preferred_time", v)
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="booking-form-time-trigger"
                                            className="mt-1 bg-white rounded-xl border-[#e8e4db] h-11"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            {TIME_SLOTS.map((t) => (
                                                <SelectItem
                                                    key={t}
                                                    value={t}
                                                >
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <Label
                                    htmlFor="bk-notes"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Anything we should know?
                                </Label>
                                <Textarea
                                    id="bk-notes"
                                    data-testid="booking-form-notes-input"
                                    value={form.notes}
                                    onChange={(e) =>
                                        update("notes", e.target.value)
                                    }
                                    placeholder="Concerns, medical history, previous treatments…"
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db] min-h-[96px]"
                                    rows={3}
                                />
                            </div>

                            <button
                                type="submit"
                                data-testid="booking-form-submit"
                                disabled={submitting}
                                className="w-full rounded-full bg-[#114b5f] text-[#faf9f6] px-6 py-4 text-sm font-medium hover:bg-[#0d3a4a] transition disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2
                                            className="animate-spin"
                                            size={16}
                                        />
                                        Submitting…
                                    </>
                                ) : (
                                    "Request Appointment"
                                )}
                            </button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
